// スキーマ駆動テンプレートの中核。
//   - Field / TemplateDef : 各テンプレートが宣言する「編集できる変数」の型
//   - 低レベルヘルパ       : escape / budoux / 見出し強調 / 画像スロット
//   - 文書組み立て         : :root 変数・共通CSS・<head>・全体HTML の生成
// フロント(App)はテンプレートの fields を読んでフォームを自動生成し、
// previewTemplate は fields を読んでライブ DOM を更新する。

import { loadDefaultJapaneseParser } from "budoux";

import {
  FONT_HREF,
  FONT_STACKS,
  type FontKind,
  type TemplateKind,
} from "../design";
import { readableText } from "../color";

// ---- スキーマ(編集できる変数)の型 --------------------------------------

// フォームのセクション見出し。同じ group のフィールドがまとめて表示される。
export type FieldGroup = "カラー" | "タイポグラフィ" | "コンテンツ" | "画像";

interface FieldBase {
  key: string; // config.values のキー。data-bind / data-img の値とも一致させる。
  label: string;
  group: FieldGroup;
}

// 色: cssVar に流し込み、onVar があればコントラスト文字色を自動で派生する。
export interface ColorField extends FieldBase {
  kind: "color";
  cssVar: string;
  onVar?: string;
  default: string;
}
// フォント: 種別を選ぶと font-family スタックを cssVar へ反映する。
export interface FontFieldDef extends FieldBase {
  kind: "font";
  cssVar: string;
  default: FontKind;
}
// テキスト(1行)。format で整形方法を切り替える。
//   headline: **強調** をブランド色 span に / inline: budoux 改行 / plain: 素の textContent
export interface TextFieldDef extends FieldBase {
  kind: "text";
  format?: "headline" | "inline" | "plain";
  default: string;
}
// テキスト(複数行)。
export interface AreaFieldDef extends FieldBase {
  kind: "textarea";
  format?: "inline" | "plain";
  default: string;
}
// 画像: クリックで差し込む data URL を格納する。
export interface ImageFieldDef extends FieldBase {
  kind: "image";
  placeholder: string;
  default?: string;
}

export type Field =
  | ColorField
  | FontFieldDef
  | TextFieldDef
  | AreaFieldDef
  | ImageFieldDef;

export type Values = Record<string, string>;

// 生成オプション。editing=true でプレビュー用の編集 UI 表現を出す。
export interface BuildOpts {
  editing?: boolean;
}

// テンプレート1つの定義。fields が変数の単一情報源、body が本文マークアップ。
export interface TemplateDef {
  id: TemplateKind;
  label: string;
  fields: Field[];
  css: string; // このテンプレート固有の <style>
  body: (v: Values, opts: BuildOpts) => string; // <body> の中身
}

// ---- 値の解決 -------------------------------------------------------------

/** 各フィールドの現在値を、未設定なら default で埋めて返す。 */
export function resolveValues(def: TemplateDef, values: Values): Values {
  const out: Values = {};
  for (const f of def.fields) {
    out[f.key] = values[f.key] ?? f.default ?? "";
  }
  return out;
}

/**
 * 既存 values を保ちつつ、テンプレートの各フィールドの default で欠けたキーを補う。
 * テンプレート切替時に、重なるキー(brandColor 等)は引き継ぎ、固有キーだけ既定値で足す。
 */
export function mergeDefaults(values: Values, def: TemplateDef): Values {
  const out: Values = { ...values };
  for (const f of def.fields) {
    if (out[f.key] === undefined) out[f.key] = f.default ?? "";
  }
  return out;
}

// ---- 低レベルヘルパ -------------------------------------------------------

/** HTMLに埋め込むテキストのエスケープ(自由入力によるプレビュー破壊を防ぐ)。 */
export function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (ch) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[ch]!,
  );
}

// BudouX の日本語パーサ(モデル同梱・同期)。iframe は sandbox でスクリプト不可のため、
// 改行位置の推定はここ(親アプリ)で行い、<wbr> 入りの静的 HTML を流し込む。
const jaParser = loadDefaultJapaneseParser();

/** テキストを文節へ分割し、escape して <wbr>(改行可能位置)で連結する。 */
export function seg(text: string): string {
  return jaParser.parse(text).map(escapeHtml).join("<wbr>");
}

/** 見出し内の **強調** をブランドカラーの <span class="hl"> に変換する。 */
export function renderHeadlineHtml(headline: string): string {
  const re = /\*\*([\s\S]+?)\*\*/g;
  const parts: string[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(headline)) !== null) {
    if (m.index > last) parts.push(seg(headline.slice(last, m.index)));
    parts.push(`<span class="hl">${seg(m[1] ?? "")}</span>`);
    last = m.index + m[0].length;
  }
  if (last < headline.length) parts.push(seg(headline.slice(last)));
  return parts.join("<wbr>");
}

/** テキストフィールドの初期 innerHTML/textContent を format に応じて整形する。 */
export function formatText(
  value: string,
  format: "headline" | "inline" | "plain" | undefined,
): string {
  if (format === "headline") return renderHeadlineHtml(value);
  if (format === "plain") return escapeHtml(value);
  return seg(value);
}

// 汎用アイコン SVG(stroke は currentColor で色を継承)。
export const svg = (paths: string, size = 26) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;

const IMG_ICON = svg(
  `<rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><path d="M21 15l-5-5L5 21"></path>`,
  30,
);

/**
 * クリックで画像を差し込めるスロットのマークアップ。data-img=key を目印に、
 * 親アプリがクリックハンドラを付け、applyValues が背景へ焼き込む。
 * url(現在値)があれば背景として反映し、has-img でプレースホルダを隠す。
 */
export function imageSlot(opts: {
  key: string;
  placeholder: string;
  className?: string;
  style?: string;
  url?: string;
}): string {
  const has = typeof opts.url === "string" && opts.url.length > 0;
  // data URL は base64 で ' を含まないため url('...') に安全に埋め込める。
  const bg = has ? `background-image:url('${opts.url}');` : "";
  const cls = `img-slot${has ? " has-img" : ""}${opts.className ? " " + opts.className : ""}`;
  const style = `${opts.style ?? ""}${bg}`;
  return `<div class="${cls}" data-img="${escapeHtml(opts.key)}" style="${style}"><div class="img-ph">${IMG_ICON}<span>${escapeHtml(opts.placeholder)}</span></div></div>`;
}

// ---- 文書の組み立て -------------------------------------------------------

// 色/フォントのフィールドから :root の初期変数宣言を作る。
// --brand/--accent/--base/--on-* と --font が揃う前提で、派生の中間色を足す。
function rootCss(def: TemplateDef, v: Values): string {
  const decls: string[] = [];
  for (const f of def.fields) {
    if (f.kind === "color") {
      decls.push(`${f.cssVar}:${v[f.key]}`);
      if (f.onVar) decls.push(`${f.onVar}:${readableText(v[f.key] ?? "#000000")}`);
    } else if (f.kind === "font") {
      decls.push(`${f.cssVar}:${FONT_STACKS[v[f.key] as FontKind]}`);
    }
  }
  return `:root{${decls.join(";")};
  --muted:color-mix(in srgb, var(--on-base) 58%, var(--base));
  --faint:color-mix(in srgb, var(--on-base) 40%, var(--base));
  --line:color-mix(in srgb, var(--on-base) 8%, var(--base));
  --surface:color-mix(in srgb, var(--on-base) 3%, var(--base));
  --card-line:color-mix(in srgb, var(--on-base) 9%, var(--base));
  --brand-soft:color-mix(in srgb, var(--brand) 7%, var(--base));
  --brand-line:color-mix(in srgb, var(--brand) 18%, var(--base));
  --brand-deep:color-mix(in srgb, var(--brand) 62%, #000);
}`;
}

// 全テンプレート共通の基本CSS(body・.bdx・.hl・画像スロット)。
const BASE_CSS = `*{box-sizing:border-box}
body{margin:0;font-family:var(--font);color:var(--on-base);line-height:1.75;background:var(--base);-webkit-font-smoothing:antialiased}
a{color:inherit}
/* BudouX: 文節境界の <wbr> だけで改行させる(任意位置の改行を禁止)。 */
.bdx{word-break:keep-all;overflow-wrap:anywhere}
.hl{color:var(--brand)}

/* 画像スロット */
.img-slot{position:relative;overflow:hidden;background-size:cover;background-position:center;background-repeat:no-repeat;background-color:var(--surface)}
.img-slot .img-ph{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center;padding:20px;color:var(--muted);border:1px dashed var(--card-line)}
.img-slot .img-ph svg{opacity:.5}
.img-slot .img-ph span{font-size:13px;font-weight:700;letter-spacing:.02em}
.img-slot.has-img .img-ph{display:none}
/* 編集モードのみ: クリック可カーソルとホバー表現 */
body.editing .img-slot{cursor:pointer}
body.editing .img-slot:not(.has-img):hover .img-ph{border-color:var(--brand);color:var(--brand);background:var(--brand-soft)}
body.editing .img-slot.has-img::after{content:"画像を変更";position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;color:#fff;background:rgba(15,23,42,.45);opacity:0;transition:opacity .15s}
body.editing .img-slot.has-img:hover::after{opacity:1}`;

// <body> の class(編集モードなら editing)。
function bodyClass(opts: BuildOpts): string {
  return opts.editing ? ` class="editing"` : "";
}

/** テンプレート定義と解決済み値から、自己完結した1枚のHTMLを組み立てる。 */
export function renderTemplate(
  def: TemplateDef,
  v: Values,
  opts: BuildOpts,
): string {
  // 初回描画から正しい書体で出せるよう、使用中フォントの <link> を先読みする。
  const fontKeys = def.fields
    .filter((f): f is FontFieldDef => f.kind === "font")
    .map((f) => v[f.key] as FontKind);
  const fontLinks = Array.from(new Set(fontKeys))
    .map(
      (fk) =>
        `<link data-font="${fk}" rel="stylesheet" href="${FONT_HREF[fk]}">`,
    )
    .join("\n");

  return `<!doctype html><html lang="ja"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
${fontLinks}
<style>${rootCss(def, v)}
${BASE_CSS}
${def.css}</style>
</head><body${bodyClass(opts)}>
${def.body(v, opts)}
</body></html>`;
}
